import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import { rolesService, Rol, Permiso } from '../../../services/rolesService'
import { resolverPresentacionErrorApi } from '../../../services/apiError'
import { useAlertas } from '../../../ganchos/useAlertas'
import { usePermisos } from '../../../ganchos/usePermisos'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import AlertaPersonalizada from '../../../componentes/interfaz-usuario/notificaciones/AlertaPersonalizada'
import SpinnerCarga from '../../../componentes/interfaz-usuario/carga/SpinnerCarga'

const PaginaRolesPermisos: React.FC = () => {
  const { tienePermiso } = usePermisos()
  const { refreshUser } = useAuth()
  const [roles, setRoles] = useState<Rol[]>([])
  const [permisos, setPermisos] = useState<Permiso[]>([])
  const [cargando, setCargando] = useState(true)
  const [modalAbierto, setModalAbierto] = useState(false)
  const [rolEditando, setRolEditando] = useState<Rol | null>(null)
  const { alerta, mostrarExito, mostrarError, cerrarAlerta } = useAlertas()

  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    permisos_ids: [] as number[]
  })

  useEffect(() => {
    cargarDatos()
  }, [])

  const cargarDatos = async () => {
    try {
      setCargando(true)
      const [rolesData, permisosData] = await Promise.all([
        rolesService.obtenerRoles(),
        rolesService.obtenerPermisos()
      ])
      setRoles(rolesData)
      setPermisos(permisosData)
    } catch (error) {
      console.error(error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al cargar datos',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          unauthorized: 'Sesión no válida',
        },
      })
      mostrarError(presentacion.title, presentacion.message)
    } finally {
      setCargando(false)
    }
  }

  const abrirModalCrear = () => {
    setRolEditando(null)
    setFormData({ nombre: '', descripcion: '', permisos_ids: [] })
    setModalAbierto(true)
  }

  const abrirModalEditar = (rol: Rol) => {
    setRolEditando(rol)
    setFormData({
      nombre: rol.nombre,
      descripcion: rol.descripcion,
      permisos_ids: rol.permisos.map(p => p.id_permiso)
    })
    setModalAbierto(true)
  }

  const cerrarModal = () => {
    setModalAbierto(false)
    setRolEditando(null)
  }

  const togglePermiso = (id: number) => {
    setFormData(prev => {
      const ids = prev.permisos_ids.includes(id)
        ? prev.permisos_ids.filter(pId => pId !== id)
        : [...prev.permisos_ids, id]
      return { ...prev, permisos_ids: ids }
    })
  }

  const guardarRol = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (rolEditando) {
        await rolesService.actualizarRol(rolEditando.id_rol, formData)
        mostrarExito('Éxito', 'Rol actualizado correctamente')
      } else {
        await rolesService.crearRol(formData)
        mostrarExito('Éxito', 'Rol creado correctamente')
      }
      
      // Actualizar permisos del usuario actual si es necesario
      await refreshUser()
      
      cerrarModal()
      cargarDatos()
    } catch (error: unknown) {
      console.error(error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al guardar rol',
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
          conflict: 'Rol duplicado',
        },
        mensajesPorCodigo: {
          validation_error: 'Verifica el nombre, la descripción y los permisos seleccionados.',
          forbidden: 'No tienes permisos para crear o modificar roles.',
        },
      })
      mostrarError(presentacion.title, presentacion.message)
    }
  }

  const eliminarRol = async (rol: Rol) => {
    if (!window.confirm(`¿Estás seguro de eliminar el rol "${rol.nombre}"?`)) return

    try {
      await rolesService.eliminarRol(rol.id_rol)
      mostrarExito('Éxito', 'Rol eliminado correctamente')
      cargarDatos()
    } catch (error: unknown) {
      console.error(error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al eliminar rol',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          not_found: 'Rol no encontrado',
          conflict: 'Operación no permitida',
        },
        mensajesPorCodigo: {
          forbidden: 'No tienes permisos para eliminar roles.',
          not_found: 'El rol que intentas eliminar ya no existe.',
        },
      })
      mostrarError(presentacion.title, presentacion.message)
    }
  }

  // Agrupar permisos por categoría
  const permisosPorCategoria = permisos.reduce((acc, permiso) => {
    const cat = permiso.categoria || 'Otros'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(permiso)
    return acc
  }, {} as Record<string, Permiso[]>)

  if (cargando) return <SpinnerCarga />

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldCheckIcon className="h-8 w-8 text-indigo-600" />
            Gestión de Roles y Permisos
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Administra los roles del sistema y sus niveles de acceso
          </p>
        </div>
        {tienePermiso('roles:crear') && (
          <button
            onClick={abrirModalCrear}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
          >
            <PlusIcon className="h-5 w-5" />
            Nuevo Rol
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map(rol => (
          <motion.div
            key={rol.id_rol}
            layout
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
          >
            <div className="p-5 flex-1">
              <div className="flex justify-between items-start mb-4">
                <div className="bg-indigo-50 dark:bg-indigo-900/30 p-2 rounded-lg">
                  <ShieldCheckIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                {rol.es_sistema && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-blue-900 dark:text-blue-300">
                    Sistema
                  </span>
                )}
              </div>
              
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {rol.nombre}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
                {rol.descripcion || 'Sin descripción'}
              </p>
              
              <div className="space-y-2">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Acceso ({rol.permisos_count} permisos)
                </div>
                <div className="flex flex-wrap gap-1">
                  {rol.permisos.slice(0, 5).map(p => (
                    <span key={p.id_permiso} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      {p.nombre}
                    </span>
                  ))}
                  {rol.permisos_count > 5 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      +{rol.permisos_count - 5} más
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/30 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              {tienePermiso('roles:editar') && (
                <button
                  onClick={() => abrirModalEditar(rol)}
                  aria-label={`Editar rol ${rol.nombre}`}
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                  title="Editar rol"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
              {!rol.es_sistema && tienePermiso('roles:eliminar') && (
                <button
                  onClick={() => eliminarRol(rol)}
                  aria-label={`Eliminar rol ${rol.nombre}`}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                  title="Eliminar rol"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal Crear/Editar */}
      <AnimatePresence>
        {modalAbierto && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {rolEditando ? 'Editar Rol' : 'Nuevo Rol'}
                </h2>
                <button onClick={cerrarModal} aria-label="Cerrar modal de roles" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={guardarRol} className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="rol-form-nombre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre del Rol
                      </label>
                      <input
                        id="rol-form-nombre"
                        name="nombre"
                        type="text"
                        value={formData.nombre}
                        onChange={e => setFormData({...formData, nombre: e.target.value})}
                        disabled={rolEditando?.es_sistema}
                        className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Ej: Editor de Contenido"
                        required
                      />
                      {rolEditando?.es_sistema && (
                        <p className="text-xs text-amber-600 mt-1">El nombre de los roles de sistema no se puede cambiar.</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="rol-form-descripcion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descripción
                      </label>
                      <textarea
                        id="rol-form-descripcion"
                        name="descripcion"
                        value={formData.descripcion}
                        onChange={e => setFormData({...formData, descripcion: e.target.value})}
                        rows={3}
                        className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Describe las responsabilidades de este rol..."
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wider">
                      Permisos Asignados
                    </h3>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                      {Object.entries(permisosPorCategoria).map(([categoria, perms]) => (
                        <div key={categoria}>
                          <h4 className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mb-2 sticky top-0 bg-gray-50 dark:bg-gray-800 py-1">
                            {categoria}
                          </h4>
                          <div className="space-y-2">
                            {perms.map(permiso => (
                              <label key={permiso.id_permiso} htmlFor={`rol-form-permiso-${permiso.id_permiso}`} className="flex items-start gap-3 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer transition-colors">
                                <input
                                  id={`rol-form-permiso-${permiso.id_permiso}`}
                                  name="permisos_ids"
                                  type="checkbox"
                                  checked={formData.permisos_ids.includes(permiso.id_permiso)}
                                  onChange={() => togglePermiso(permiso.id_permiso)}
                                  className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {permiso.nombre}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {permiso.descripcion}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </form>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800/50">
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarRol}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center gap-2"
                >
                  <CheckIcon className="h-4 w-4" />
                  {rolEditando ? 'Guardar Cambios' : 'Crear Rol'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {alerta && (
        <AlertaPersonalizada
          {...alerta}
          onCerrar={cerrarAlerta}
        />
      )}
    </div>
  )
}

export default PaginaRolesPermisos
